import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import Modal from '@/components/base/Modal';
import { useToast } from '@/components/base/Toast';

// ---------- Tipos ----------
interface Partida {
  id: string;
  conceptoNombre: string;
  unidad: string;
  cantidad: number;
  precioUnitario: number;
  subtotal: number;
  esVariable: boolean;
  moneda: 'MXN' | 'USD';
  precioProveedor: number | null;
}

interface Cotizacion {
  id: string;
  obraId: string;
  obraNombre: string;
  clienteNombre: string;
  clienteTelefono?: string;
  numero: string;
  fecha: string;
  estado: 'pendiente' | 'autorizada' | 'rechazada';
  monto: number;
  partidas: Partida[];
  autorizadoPor: string | null;
  ivaPorcentaje?: number;
  totalMXN?: number;
  totalUSD?: number;
  ivaMXN?: number;
  ivaUSD?: number;
  totalMXNConIVA?: number;
  totalUSDConIVA?: number;
  gananciaMXN?: number;
  gananciaUSD?: number;
  pdfUrl?: string;
}

interface ObraReciente {
  id: string;
  nombre: string;
  estado: string;
}

interface Actividad {
  id: string;
  tipo: string;
  descripcion: string;
  fecha: string;
}

interface DashboardStats {
  totalClientes: number;
  obrasActivas: number;
  cotizacionesPendientes: number;
  montoCotizadoTotal: number;
  montoCotizadoMXN?: number;
  montoCotizadoUSD?: number;
}

// ---------- Constantes y helpers ----------
const API_URL = 'https://api.airmex.mx/api/v1';

function getToken() {
  return localStorage.getItem('airmex_token') || sessionStorage.getItem('airmex_token');
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...((options.headers as Record<string, string>) || {}),
  };
  const res = await fetch(`${API_URL}${url}`, { ...options, headers });
  if (!res.ok) {
    let mensaje = 'Error en la petición';
    try {
      const errorData = await res.json();
      if (errorData.detail) {
        if (Array.isArray(errorData.detail)) {
          mensaje = errorData.detail
            .map((e: any) => {
              const campo = e.loc ? e.loc.join('.') : '';
              return campo ? `${campo}: ${e.msg}` : e.msg;
            })
            .join(' | ');
        } else if (typeof errorData.detail === 'object') {
          mensaje = JSON.stringify(errorData.detail);
        } else {
          mensaje = errorData.detail;
        }
      } else if (errorData.message) {
        mensaje = errorData.message;
      }
    } catch {
      // no JSON
    }
    throw new Error(mensaje);
  }
  if (res.status === 204) return null;
  return res.json();
}

const abrirWhatsApp = (cot: Cotizacion) => {
  const telefono = (cot.clienteTelefono || '').replace(/\D/g, '');
  if (!telefono) {
    showToast('warning', 'Sin teléfono', 'El cliente no tiene número de teléfono.');
    return;
  }
  const telefonoCompleto = telefono.startsWith('52') ? telefono : `52${telefono}`;
  let mensaje = `Hola, le comparto la cotización ${cot.numero} para la obra ${cot.obraNombre}.\n`;
  if (cot.pdfUrl) {
    mensaje += `Descargue su cotización aquí: ${cot.pdfUrl}`;
  } else {
    mensaje += `Total MXN: ${formatMonto(cot.monto)}`;
  }
  const url = `https://wa.me/${telefonoCompleto}?text=${encodeURIComponent(mensaje)}`;
  window.open(url, '_blank');
};

function formatMonto(monto: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(monto);
}

function formatUSD(monto: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(monto);
}

const estadoCotizacionColors: Record<string, string> = {
  pendiente: 'bg-amber-100 text-amber-700',
  autorizada: 'bg-emerald-100 text-emerald-700',
  rechazada: 'bg-red-100 text-red-600',
};

const actividadIcons: Record<string, string> = {
  cotizacion: 'ri-file-list-3-line',
  obra: 'ri-building-line',
  archivo: 'ri-file-upload-line',
  autorizacion: 'ri-check-double-line',
  cliente: 'ri-user-add-line',
  factura: 'ri-bill-line',
};

const actividadColors: Record<string, string> = {
  cotizacion: 'bg-primary-100 text-primary-600',
  obra: 'bg-accent-100 text-accent-600',
  archivo: 'bg-secondary-100 text-secondary-600',
  autorizacion: 'bg-accent-100 text-accent-600',
  cliente: 'bg-primary-100 text-primary-600',
  factura: 'bg-amber-100 text-amber-600',
};

export default function DashboardPage() {
  const { usuario, cargando: authCargando } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [obrasRecientes, setObrasRecientes] = useState<ObraReciente[]>([]);
  const [cotizacionesRecientes, setCotizacionesRecientes] = useState<Cotizacion[]>([]);
  const [cotizacionesPendientes, setCotizacionesPendientes] = useState<Cotizacion[]>([]);
  const [actividadReciente, setActividadReciente] = useState<Actividad[]>([]);
  const [detalleCotizacion, setDetalleCotizacion] = useState<Cotizacion | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Redirigir si no hay usuario
  useEffect(() => {
    if (!authCargando && !usuario) {
      navigate('/login', { replace: true });
    }
  }, [authCargando, usuario, navigate]);

  const loadDashboardData = async () => {
    try {
      setCargando(true);
      setError(null);

      const [statsData, obras, cotizaciones, actividad] = await Promise.all([
        fetchWithAuth('/dashboard/stats'),
        fetchWithAuth('/recientes?limit=5'),
        fetchWithAuth('/cotizaciones/?limit=10'),
        fetchWithAuth('/actividad/?limit=10'),
      ]);

      setStats(statsData);
      setObrasRecientes(obras);
      setActividadReciente(actividad);

      const pendientes = (cotizaciones as Cotizacion[]).filter(c => c.estado === 'pendiente');
      const recientes = (cotizaciones as Cotizacion[]).filter(c => c.estado !== 'pendiente').slice(0, 5);
      setCotizacionesPendientes(pendientes);
      setCotizacionesRecientes(recientes);
    } catch (err: any) {
      setError(err.message || 'Error al cargar datos');
      showToast('error', 'Error al cargar el panel', err.message);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (usuario) {
      loadDashboardData();
    }
  }, [usuario]);

  const handleAprobar = async (cot: Cotizacion) => {
  try {
    const updated = await fetchWithAuth(
      `/cotizaciones/${cot.id}?estado=autorizada&autorizadoPor=${encodeURIComponent(usuario?.nombre || 'Admin')}`,
      { method: 'PUT' }
    );
    setCotizacionesPendientes(prev => prev.filter(c => c.id !== cot.id));
    setCotizacionesRecientes(prev => [updated, ...prev]);
    const statsData = await fetchWithAuth('/dashboard/stats');
    setStats(statsData);
    showToast('success', 'Cotización autorizada', `${cot.numero} ha sido aprobada.`);
    abrirWhatsApp(updated);   // ← abrir WhatsApp con la cotización actualizada
  } catch (err: any) {
    showToast('error', 'Error al aprobar', err.message);
  }
};

  const handleRechazar = async (cot: Cotizacion) => {
    try {
      const updated = await fetchWithAuth(`/cotizaciones/${cot.id}?estado=rechazada`, { method: 'PUT' });
      setCotizacionesPendientes(prev => prev.filter(c => c.id !== cot.id));
      setCotizacionesRecientes(prev => [updated, ...prev]);
      const statsData = await fetchWithAuth('/dashboard/stats');
      setStats(statsData);
      showToast('info', 'Cotización rechazada', `${cot.numero} ha sido rechazada.`);
    } catch (err: any) {
      showToast('error', 'Error al rechazar', err.message);
    }
  };

  if (authCargando || cargando) {
    return (
      <div className="flex justify-center items-center h-screen bg-background-100">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 mb-4 block" />
          <p className="text-foreground-500 text-sm">Cargando panel...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-96">
          <i className="ri-error-warning-line text-4xl text-red-500 mb-4" />
          <h2 className="text-lg font-semibold text-foreground-800">Error al cargar el panel</h2>
          <p className="text-sm text-foreground-500 mt-2">{error}</p>
          <button onClick={loadDashboardData} className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm">
            Reintentar
          </button>
        </div>
      </DashboardLayout>
    );
  }

  if (!usuario) return null;

  const statCards = [
    {
      label: 'Clientes totales',
      value: stats?.totalClientes || 0,
      icon: 'ri-user-star-line',
      color: 'primary',
      change: 'Actual',
      custom: false,
    },
    {
      label: 'Obras activas',
      value: stats?.obrasActivas || 0,
      icon: 'ri-building-line',
      color: 'accent',
      change: 'En progreso',
      custom: false,
    },
    {
      label: 'Cotizaciones pendientes',
      value: stats?.cotizacionesPendientes || 0,
      icon: 'ri-file-list-3-line',
      color: 'amber',
      change: 'Requieren atención',
      custom: false,
    },
    {
      label: 'Monto total cotizado',
      icon: 'ri-money-dollar-circle-line',
      color: 'emerald',
      change: 'Acumulado',
      custom: true,
    },
  ];

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto space-y-6">
        {/* Cabecera */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">
              Bienvenido, {usuario?.nombre ? usuario.nombre.split(' ')[0] : usuario?.email}
            </h1>
            <p className="text-sm text-foreground-500 mt-0.5 capitalize">
              Panel de {usuario?.rol ?? 'sin rol'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground-400">
              {new Date().toLocaleDateString('es-MX', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>
        </div>

        {/* Tarjetas de estadísticas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((card, i) => (
            <div key={i} className="bg-background-50 rounded-xl border border-background-200 p-4 hover:border-background-300 transition-colors">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-foreground-400 font-medium">{card.label}</p>
                  {card.custom ? (
                    <div className="mt-1.5 space-y-1">
                      <p className="text-sm font-semibold text-foreground-900">
                        {formatMonto(stats?.montoCotizadoMXN ?? 0)}
                      </p>
                      <p className="text-sm font-semibold text-foreground-900">
                        {formatUSD(stats?.montoCotizadoUSD ?? 0)}
                      </p>
                    </div>
                  ) : (
                    <p className="font-heading text-xl font-semibold text-foreground-900 mt-1.5">
                      {card.value}
                    </p>
                  )}
                  <p className="text-[11px] text-foreground-400 mt-1">{card.change}</p>
                </div>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  card.color === 'primary' ? 'bg-primary-100 text-primary-600' :
                  card.color === 'accent' ? 'bg-accent-100 text-accent-600' :
                  card.color === 'amber' ? 'bg-amber-100 text-amber-600' :
                  'bg-emerald-100 text-emerald-600'
                }`}>
                  <i className={`${card.icon} text-lg`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Cotizaciones pendientes (solo admin) */}
        {usuario?.rol === 'admin' && cotizacionesPendientes.length > 0 && (
          <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-amber-200 bg-amber-50/50">
              <div className="flex items-center gap-2">
                <i className="ri-alert-line text-amber-600" />
                <h2 className="font-heading font-semibold text-sm text-foreground-800">
                  Cotizaciones pendientes de autorización
                </h2>
                <span className="ml-1 inline-flex items-center justify-center min-w-[22px] h-5 rounded-full bg-amber-500 text-background-50 text-[10px] font-bold px-1.5">
                  {cotizacionesPendientes.length}
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/50">
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-foreground-500 whitespace-nowrap">Número</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-foreground-500 whitespace-nowrap">Obra</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-foreground-500 whitespace-nowrap">Cliente</th>
                    <th className="text-right px-4 py-2.5 text-[11px] font-semibold text-foreground-500 whitespace-nowrap">Monto</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-foreground-500 whitespace-nowrap">Fecha</th>
                    <th className="text-center px-4 py-2.5 text-[11px] font-semibold text-foreground-500 whitespace-nowrap">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {cotizacionesPendientes.map((cot) => (
                    <tr key={cot.id} className="hover:bg-background-50/50 transition-colors">
                      <td className="px-4 py-3 text-xs font-mono font-medium text-foreground-700">{cot.numero}</td>
                      <td className="px-4 py-3 text-xs text-foreground-600 max-w-[200px] truncate">{cot.obraNombre}</td>
                      <td className="px-4 py-3 text-xs text-foreground-600">{cot.clienteNombre}</td>
                      <td className="px-4 py-3 text-right text-xs font-semibold text-foreground-800 font-mono">
                        {formatMonto(cot.totalMXNConIVA ?? cot.monto)}
                        {cot.totalUSDConIVA != null && cot.totalUSDConIVA > 0 && (
                          <span className="block text-[10px] text-foreground-500">{formatUSD(cot.totalUSDConIVA)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-foreground-400">{cot.fecha}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => setDetalleCotizacion(cot)} className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                            <i className="ri-eye-line mr-1" />Ver
                          </button>
                          <button onClick={() => handleAprobar(cot)} className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap">
                            <i className="ri-check-line mr-1" />Aprobar
                          </button>
                          <button onClick={() => handleRechazar(cot)} className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-red-500 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap">
                            <i className="ri-close-line mr-1" />Rechazar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Obras y cotizaciones recientes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Obras recientes */}
          <div className="bg-background-50 rounded-xl border border-background-200">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-background-200">
              <i className="ri-building-line text-foreground-600" />
              <h3 className="font-heading font-semibold text-sm text-foreground-800">Obras recientes</h3>
            </div>
            <div className="divide-y divide-background-100">
              {obrasRecientes.length === 0 ? (
                <div className="p-4 text-xs text-foreground-400 text-center">No hay obras registradas.</div>
              ) : (
                obrasRecientes.map((obra) => (
                  <div key={obra.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-background-50/50 transition-colors cursor-pointer">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-800 truncate">{obra.nombre}</p>
                    </div>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${obra.estado === 'activa' ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-500'}`}>
                      {obra.estado}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Cotizaciones recientes */}
          <div className="bg-background-50 rounded-xl border border-background-200">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-background-200">
              <i className="ri-file-list-3-line text-foreground-600" />
              <h3 className="font-heading font-semibold text-sm text-foreground-800">Cotizaciones recientes</h3>
            </div>
            <div className="divide-y divide-background-100">
              {cotizacionesRecientes.length === 0 ? (
                <div className="p-4 text-xs text-foreground-400 text-center">No hay cotizaciones recientes.</div>
              ) : (
                cotizacionesRecientes.map((cot) => (
                  <div key={cot.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-background-50/50 transition-colors cursor-pointer" onClick={() => setDetalleCotizacion(cot)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-medium text-foreground-600">{cot.numero}</span>
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${estadoCotizacionColors[cot.estado]}`}>
                          {cot.estado}
                        </span>
                      </div>
                      <p className="text-xs text-foreground-400 mt-0.5 truncate">{cot.obraNombre}</p>
                    </div>
                    <div className="text-right flex-shrink-0 space-y-0.5">
                      <p className="text-sm font-semibold text-foreground-800">{formatMonto(cot.totalMXNConIVA ?? cot.monto)}</p>
                      {cot.totalUSDConIVA != null && cot.totalUSDConIVA > 0 && (
                        <p className="text-xs font-medium text-foreground-600">{formatUSD(cot.totalUSDConIVA)}</p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Actividad reciente */}
        <div className="bg-background-50 rounded-xl border border-background-200">
          <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
            <div className="flex items-center gap-2">
              <i className="ri-history-line text-foreground-600" />
              <h3 className="font-heading font-semibold text-sm text-foreground-800">Actividad reciente</h3>
            </div>
          </div>
          <div className="px-5 py-3 space-y-0">
            {actividadReciente.length === 0 ? (
              <div className="py-4 text-xs text-foreground-400 text-center">No hay actividad registrada.</div>
            ) : (
              actividadReciente.map((act) => (
                <div key={act.id} className="flex items-center gap-3 py-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${actividadColors[act.tipo] || 'bg-background-100'}`}>
                    <i className={`${actividadIcons[act.tipo] || 'ri-file-line'} text-sm`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground-700">{act.descripcion}</p>
                  </div>
                  <span className="text-[11px] text-foreground-400 flex-shrink-0">{act.fecha}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Modal detalle de cotización */}
      <Modal
        open={!!detalleCotizacion}
        onClose={() => setDetalleCotizacion(null)}
        title={detalleCotizacion ? `Detalle de Cotización — ${detalleCotizacion.numero}` : ''}
        size="xl"
        footer={
          <>
            <button
              onClick={() => setDetalleCotizacion(null)}
              className="px-4 py-2 rounded-lg border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cerrar
            </button>
            {detalleCotizacion && detalleCotizacion.estado === 'pendiente' && (
              <>
                <button
                  onClick={() => {
                    handleRechazar(detalleCotizacion);
                    setDetalleCotizacion(null);
                  }}
                  className="px-4 py-2 rounded-lg border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-line mr-1" />Rechazar
                </button>
                <button
                  onClick={() => {
                    handleAprobar(detalleCotizacion);
                    setDetalleCotizacion(null);
                  }}
                  className="px-5 py-2 rounded-lg bg-emerald-500 text-background-50 text-sm font-medium hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-check-line mr-1" />Autorizar
                </button>
              </>
            )}
          </>
        }
      >
        {detalleCotizacion && (
          <div className="space-y-4">
            {/* Información general */}
            <div className="flex flex-wrap gap-3 text-xs">
              <div className="bg-background-100 px-3 py-1.5 rounded-lg flex items-center gap-1.5">
                <i className="ri-building-line text-foreground-400" />
                <span className="text-foreground-600 font-medium">{detalleCotizacion.obraNombre}</span>
              </div>
              <div className="bg-background-100 px-3 py-1.5 rounded-lg flex items-center gap-1.5">
                <i className="ri-user-line text-foreground-400" />
                <span className="text-foreground-600 font-medium">{detalleCotizacion.clienteNombre}</span>
              </div>
              <div className="bg-background-100 px-3 py-1.5 rounded-lg flex items-center gap-1.5">
                <i className="ri-calendar-line text-foreground-400" />
                <span className="text-foreground-600">{detalleCotizacion.fecha}</span>
              </div>
              <div className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 ${estadoCotizacionColors[detalleCotizacion.estado]}`}>
                <i className={`text-xs ${detalleCotizacion.estado === 'pendiente' ? 'ri-time-line' : detalleCotizacion.estado === 'autorizada' ? 'ri-check-double-line' : 'ri-close-circle-line'}`} />
                <span className="font-medium capitalize">{detalleCotizacion.estado}</span>
              </div>
            </div>

            {/* Tabla de partidas */}
            <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-200 bg-background-100/50">
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-foreground-500 whitespace-nowrap">Concepto</th>
                      <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-foreground-500 whitespace-nowrap">Unidad</th>
                      <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-foreground-500 whitespace-nowrap">Cant.</th>
                      <th className="text-right px-3 py-2.5 text-[11px] font-semibold text-foreground-500 whitespace-nowrap">P. Público</th>
                      <th className="text-right px-3 py-2.5 text-[11px] font-semibold text-foreground-500 whitespace-nowrap">P. Proveedor</th>
                      <th className="text-right px-3 py-2.5 text-[11px] font-semibold text-foreground-500 whitespace-nowrap">Subtotal</th>
                      <th className="text-right px-3 py-2.5 text-[11px] font-semibold text-foreground-500 whitespace-nowrap">Ganancia</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-100">
                    {detalleCotizacion.partidas.map((p) => {
                      const esUSD = p.moneda === 'USD';
                      const ganancia = p.precioProveedor != null ? (p.precioUnitario - p.precioProveedor) * p.cantidad : null;
                      return (
                        <tr key={p.id}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs text-foreground-700">{p.conceptoNombre}</span>
                              {p.esVariable && (
                                <span className="inline-block px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-medium whitespace-nowrap">
                                  Var
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-center text-xs text-foreground-500">{p.unidad}</td>
                          <td className="px-3 py-2.5 text-center text-xs text-foreground-700 font-mono">{p.cantidad}</td>
                          <td className="px-3 py-2.5 text-right text-xs text-foreground-700 font-mono">
                            {esUSD ? formatUSD(p.precioUnitario) : formatMonto(p.precioUnitario)}
                          </td>
                          <td className="px-3 py-2.5 text-right text-xs text-foreground-700 font-mono">
                            {p.precioProveedor != null ? (esUSD ? formatUSD(p.precioProveedor) : formatMonto(p.precioProveedor)) : '—'}
                          </td>
                          <td className="px-3 py-2.5 text-right text-xs text-foreground-800 font-semibold font-mono">
                            {esUSD ? formatUSD(p.subtotal) : formatMonto(p.subtotal)}
                          </td>
                          <td className="px-3 py-2.5 text-right text-xs font-semibold text-green-600 font-mono">
                            {ganancia != null ? (esUSD ? formatUSD(ganancia) : formatMonto(ganancia)) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Resumen de totales */}
            {detalleCotizacion.totalMXN != null || detalleCotizacion.totalUSD != null ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-background-200 p-4">
                  <p className="text-xs text-foreground-400">Total MXN (sin IVA)</p>
                  <p className="text-xl font-bold text-foreground-900">{formatMonto(detalleCotizacion.totalMXN ?? 0)}</p>
                  {detalleCotizacion.ivaMXN != null && (
                    <>
                      <p className="text-xs text-foreground-400 mt-1">IVA ({detalleCotizacion.ivaPorcentaje ?? 0}%)</p>
                      <p className="text-sm font-semibold text-foreground-700">{formatMonto(detalleCotizacion.ivaMXN)}</p>
                      <p className="text-xs text-foreground-400 mt-1">Total MXN con IVA</p>
                      <p className="text-lg font-bold text-foreground-900">{formatMonto(detalleCotizacion.totalMXNConIVA ?? 0)}</p>
                    </>
                  )}
                </div>
                <div className="bg-white rounded-xl border border-background-200 p-4">
                  <p className="text-xs text-foreground-400">Total USD (sin IVA)</p>
                  <p className="text-xl font-bold text-foreground-900">{formatUSD(detalleCotizacion.totalUSD ?? 0)}</p>
                  {detalleCotizacion.ivaUSD != null && (
                    <>
                      <p className="text-xs text-foreground-400 mt-1">IVA ({detalleCotizacion.ivaPorcentaje ?? 0}%)</p>
                      <p className="text-sm font-semibold text-foreground-700">{formatUSD(detalleCotizacion.ivaUSD)}</p>
                      <p className="text-xs text-foreground-400 mt-1">Total USD con IVA</p>
                      <p className="text-lg font-bold text-foreground-900">{formatUSD(detalleCotizacion.totalUSDConIVA ?? 0)}</p>
                    </>
                  )}
                </div>
                <div className="bg-white rounded-xl border border-background-200 p-4">
                  <p className="text-xs text-foreground-400">Ganancias</p>
                  <p className="text-sm font-semibold text-green-600">MXN: {formatMonto(detalleCotizacion.gananciaMXN ?? 0)}</p>
                  <p className="text-sm font-semibold text-green-600">USD: {formatUSD(detalleCotizacion.gananciaUSD ?? 0)}</p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-background-200 p-4">
                <p className="text-xs text-foreground-400">Total</p>
                <p className="text-xl font-bold text-foreground-900">{formatMonto(detalleCotizacion.monto)}</p>
              </div>
            )}

            {detalleCotizacion.autorizadoPor && (
              <p className="text-xs text-foreground-400 flex items-center gap-1.5">
                <i className="ri-check-double-line text-emerald-500" />
                Autorizado por: <span className="font-medium text-foreground-600">{detalleCotizacion.autorizadoPor}</span>
              </p>
            )}
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
}